import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Plus } from "lucide-react";
import { fetcher, imgUrl, GENDERS, formatDate } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { logRequest, singleMessage, waLink } from "../../lib/whatsapp";
import { Badges, Price } from "../../components/public/ProductCard";
import { ProductGrid, SectionTitle } from "../../components/public/ProductGrid";
import { WhatsAppIcon } from "../../components/public/WhatsAppIcon";

function Gallery({ images, name }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    setActive(0);
  }, [images]);
  if (!images?.length) return <div className="arabesque flex aspect-[4/5] items-center justify-center rounded-2xl border border-[#D4AF37]/15 font-serif text-3xl text-[#D4AF37]/40">EMAIA</div>;
  return (
    <div className="flex flex-col-reverse gap-4 md:flex-row">
      {images.length > 1 && (
        <div className="no-scrollbar flex gap-3 overflow-x-auto md:w-20 md:flex-col">
          {images.map((src, i) => (
            <button key={src} onClick={() => setActive(i)} className={`h-24 w-20 shrink-0 overflow-hidden rounded-lg border transition-[border-color,opacity] ${i === active ? "border-[#D4AF37]" : "border-white/10 opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${i}`}>
              <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="relative aspect-[4/5] flex-1 overflow-hidden rounded-2xl border border-[#D4AF37]/15 bg-black">
        <motion.img key={images[active]} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} src={imgUrl(images[active])} alt={name} className="h-full w-full object-cover" data-testid="gallery-main-image" />
      </div>
    </div>
  );
}

const NOTES = [["notes_top", "Notas de saída"], ["notes_heart", "Notas de coração"], ["notes_base", "Notas de fundo"]];

export default function ProductPage() {
  const { slug } = useParams();
  const { settings, toggleItem, isSelected } = useStore();
  const { data, error } = useSWR(`/public/products/${slug}`, fetcher);
  const p = data?.product;

  useEffect(() => {
    if (!p) return;
    document.title = `${p.name} — ${p.brand} | EMAIA PERFUMES`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", (p.description || `${p.name} ${p.brand} ${p.volume_ml}ml`).slice(0, 160));
  }, [p]);

  if (error) return (
    <div className="py-32 text-center" data-testid="product-not-found">
      <p className="font-serif text-4xl">Perfume não encontrado</p>
      <Link to="/catalogo" className="btn-outline-gold mt-8">Voltar ao catálogo</Link>
    </div>
  );
  if (!p) return <div className="py-32 text-center text-sm text-[#A1A1AA]">Carregando…</div>;
  const selected = isSelected(p.id);

  return (
    <div className="mx-auto max-w-7xl px-5 pt-10 lg:px-8" data-testid="product-page">
      <Link to="/catalogo" className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#F3E5AB]" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> Catálogo</Link>
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Gallery images={p.images} name={p.name} />
        <div className="fade-up">
          <Badges p={p} className="mb-5" />
          <p className="eyebrow">{p.brand}</p>
          <h1 className="mt-3 font-serif text-4xl font-medium leading-tight text-[#FAF6F0] sm:text-5xl lg:text-6xl" data-testid="product-title">{p.name}</h1>
          <p className="mt-3 text-sm text-[#A1A1AA]">{p.volume_ml}ml · {GENDERS[p.gender]}{p.family ? ` · ${p.family}` : ""}</p>
          <div className="mt-8"><Price p={p} big /></div>
          {p.promo_ends_at && <p className="mt-2 text-xs text-[#D4AF37]">Oferta válida até {formatDate(p.promo_ends_at, true)}</p>}
          <p className={`mt-3 text-sm ${p.in_stock ? "text-emerald-400" : "text-[#A1A1AA]"}`} data-testid="product-stock">{p.in_stock ? "Disponível em estoque" : "Sob consulta — confirme a disponibilidade"}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href={settings?.whatsapp ? waLink(settings.whatsapp, singleMessage(p)) : "#"} target="_blank" rel="noopener noreferrer" onClick={() => logRequest([{ id: p.id, qty: 1 }])} className="btn-whatsapp flex-1 py-4" data-testid="product-detail-whatsapp-button">
              <WhatsAppIcon /> Comprar pelo WhatsApp
            </a>
            <button onClick={() => toggleItem(p)} className={selected ? "btn-gold" : "btn-outline-gold"} data-testid="product-detail-add-selection">
              {selected ? <><Check className="h-4 w-4" /> Na seleção</> : <><Plus className="h-4 w-4" /> Adicionar à seleção</>}
            </button>
          </div>

          {p.description && <p className="mt-10 whitespace-pre-line leading-relaxed text-[#C9C4BA]" data-testid="product-description">{p.description}</p>}

          {NOTES.some(([k]) => p[k]) && (
            <div className="mt-10 rounded-2xl border border-[#D4AF37]/15 bg-[#141417] p-6" data-testid="product-notes">
              <p className="eyebrow mb-5">Pirâmide olfativa</p>
              {NOTES.filter(([k]) => p[k]).map(([k, label], i) => (
                <div key={k} className={`flex gap-5 py-4 ${i ? "border-t border-white/5" : ""}`}>
                  <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 font-serif text-sm text-[#D4AF37]">{i + 1}</span>
                  <div><p className="font-serif text-xl text-[#F3E5AB]">{label}</p><p className="mt-1 text-sm text-[#A1A1AA]">{p[k]}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <section className="pt-24">
          <SectionTitle eyebrow="Você também pode gostar" title="Fragrâncias relacionadas" />
          <ProductGrid items={data.related} testid="related-grid" />
        </section>
      )}
    </div>
  );
}
